import { useToggle } from './useToggle';
import { getFromLocalStorage, saveToLocalStorage } from './utils';

/**
 * A hook that returns a boolean value and a function to toggle it.
 * The value is persisted in local storage.
 *
 * @param initialValue - The initial value of the toggle.
 * @param namespace - The namespace of the local storage key.
 * @param key - The key of the local storage value.
 * @returns A tuple containing the boolean value and the function to toggle it.
 *
 * Example:
 * const [collapsed, toggleCollapsed] = useLocalStorageToggle({
 *   initialValue: true,
 *   namespace: 'my-namespace',
 *   key: 'my-key',
 * });
 */
function useLocalStorageToggle({
  initialValue,
  namespace,
  key,
}: {
  initialValue: boolean;
  namespace: string;
  key: string;
}): [boolean, () => void] {
  const localValue = getFromLocalStorage<boolean>(namespace, key);
  const [value, toggleValue] = useToggle(localValue ?? initialValue);

  function handleLocalStorageToggle(): void {
    toggleValue();
    saveToLocalStorage(namespace, key, !value);
  }

  return [value, handleLocalStorageToggle];
}

export default useLocalStorageToggle;
